import { Controller } from "@hotwired/stimulus";
import { autocomplete } from '@algolia/autocomplete-js';
import { BaseItem, AutocompleteState } from "@algolia/autocomplete-core";
import { Loader } from "@googlemaps/js-api-loader";

export default class extends Controller {
  static targets = ["addressInput", "addressResult", "addressLatitude", "addressLongitude"]

  declare hasAddressInputTarget: boolean;
  declare addressInputTarget: HTMLInputElement;
  declare addressResultTarget: HTMLInputElement;
  declare addressLatitudeTarget: HTMLInputElement;
  declare addressLongitudeTarget: HTMLInputElement;

  autocompleteApi?: { setQuery: (query: string) => void; destroy: () => void };
  sessionToken?: google.maps.places.AutocompleteSessionToken;
  selectedValue = "";
  inputChanged = false;
  mapsLoaded?: Promise<void>;

  connect(): void {
    const mapsKey = this.data.get("maps-key");
    if (mapsKey) {
      this.mapsLoaded = this.loadMaps(mapsKey);
    }

    this.initAutocomplete();
  }

  disconnect(): void {
    this.autocompleteApi?.destroy();
    this.autocompleteApi = undefined;
  }

  private async loadMaps(mapsApiKey: string): Promise<void> {
    const loader = new Loader({ apiKey: mapsApiKey, region: "NZ", language: "en" });
    await loader.load();
    await google.maps.importLibrary("places");
  }

  private initAutocomplete(): void {
    if (this.hasAddressInputTarget && this.addressInputTarget) {
      this.selectedValue = this.data.get("current-address") ?? this.addressResultTarget.value;

      this.autocompleteApi ??= autocomplete({
        container: this.addressInputTarget,
        panelContainer: this.addressInputTarget,
        classNames: {
          form: "input",
          item: "p-2",
        },
        id: "autocomplete-address",
        autoFocus: true,
        defaultActiveItemId: 0,
        initialState: { query: this.selectedValue },
        onStateChange: ({ state } : { state: AutocompleteState<BaseItem> }) => {
          this.inputChanged ||= (state.query != this.selectedValue);
          if (this.inputChanged) this.autocompleteInputChanged();
        },
        shouldPanelOpen: ({ state } : { state: AutocompleteState<BaseItem> }) => {
          return (this.inputChanged && state.query != "");
        },
        getSources: () => {
          return [{
            sourceId: "autocomplete-address", // arbitrary
            getItems: async ({ query } : { query: string }): Promise<BaseItem[]> => {
              await this.mapsLoaded;

              // Start a new session if we don't have one active, so we can get combined session pricing on the completion
              this.sessionToken ??= new google.maps.places.AutocompleteSessionToken();

              const result = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(this.autocompleteRequest(query));
              return result.suggestions.map((suggestion) => { return { id: suggestion.placePrediction?.placeId, prediction: suggestion.placePrediction } });
            },
            templates: {
              item({ item, html }) {
                const prediction = (item.prediction as google.maps.places.PlacePrediction);
                return html`<div>${prediction.mainText?.text}</div><div class="description">${prediction.secondaryText?.text}</div>`;
              },
              noResults({ state, html }) {
                return html`Sorry, couldn't find an address for "${state.query}"`;
              },
            },
            onSelect: ({ item }) => {
              // Calling toPlace() terminates the session, and we want to reset or else we'll get charged per-request thereafter.
              this.sessionToken = undefined;
              void this.applyPrediction(item.prediction as google.maps.places.PlacePrediction);
            },
          }];
        },
      });

      // Although we set autoFocus: true above, that doesn't work reliably on Chrome when Algolia is slow to create the input
      this.focusAutocomplete();
    }
  }

  focusAutocomplete(): void {
    const input = document.getElementById('autocomplete-address-input') as HTMLInputElement;
    if (input.checkVisibility()) {
      input?.focus();
      input?.select();
    }
  }

  autocompleteInputChanged(): void {
  }

  autocompleteRequest(query: string): google.maps.places.AutocompleteRequest {
    return {
      input: query,
      includedPrimaryTypes: [
        // This is our attempt to say "a specific place on a street, not a whole street or suburb"
        "street_address", "premise", "subpremise",

        // Frustratingly a few stores, particularly big ones, say they're on the corner of X and Y,
        // which is not great because we get the coords for the actual middle of the intersection.
        // We're trying to replace this with the custom-data mechanism and malls, so maybe we can
        // remove it some day.
        "intersection",
      ],
      includedRegionCodes: ["nz"],
      language: "en-NZ",
      region: "nz",
      sessionToken: this.sessionToken,
    };
  }

  async applyPrediction(prediction: google.maps.places.PlacePrediction): Promise<void> {
    const place = prediction.toPlace();
    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });

    this.applyPlace(place);

    // We also update the input field to the "full" version of the selected value, which has the city etc. in it,
    // because the default behavior is just to select the short version which isn't what we'll actually use.
    this.inputChanged = false;
    this.autocompleteApi?.setQuery(this.selectedValue);
  }

  applyPlace(place: google.maps.places.Place): void {
    // We have a separate field for what we actually submit in params because we want to make sure that the
    // submitted address_for_maps corresponds to the submitted latitude & longitude; putting the autocomplete
    // on an address_for_maps input itself nearly works but unfortunately then if the admin doesn't actually
    // choose from the autocomplete list, the place doesn't get updated. We'd rather lose the incomplete
    // address change than have not-matching data.
    this.addressResultTarget.value = place.formattedAddress!;
    this.addressLatitudeTarget.value = place.location!.lat().toFixed(7);
    this.addressLongitudeTarget.value = place.location!.lng().toFixed(7);
    this.selectedValue = place.formattedAddress!;
  }
}
